import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Gift, Crown, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * Dashboard tile showing progress toward the 3-invites-> 1-month-Silver reward.
 * Renders three visual states:
 *   1) Not yet started / in progress (0..2 joined)
 *   2) Reward active (Silver granted, expiry date shown)
 *   3) Reward already used and expired (encourage upgrade)
 */
export default function ReferralRewardCard() {
  const [prog, setProg] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/referrals/progress");
        if (alive) setProg(data);
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!prog) {
    return (
      <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-white border border-brand-line p-8" data-testid="referral-reward-loading">
        <div className="label-eyebrow text-brand-muted">Referral rewards</div>
        <div className="mt-4 h-16 animate-pulse bg-brand-line/40" />
      </div>
    );
  }

  const { joined, threshold, remaining, reward_active, reward_ever_granted, reward_expires_at } = prog;
  const pct = Math.min(100, Math.round((joined / threshold) * 100));
  const expiryDate = reward_expires_at
    ? new Date(reward_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;

  // STATE 3: Reward already claimed and now expired
  if (reward_ever_granted && !reward_active) {
    return (
      <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-brand-bone border border-brand-line p-8" data-testid="referral-reward-expired">
        <div className="flex items-center gap-2">
          <Gift size={20} weight="duotone" className="text-brand-terracotta" />
          <div className="label-eyebrow text-brand-terracotta">Referral rewards</div>
        </div>
        <h3 className="font-serif text-2xl mt-3 text-brand-ink leading-tight">
          Your complimentary Silver has ended.
        </h3>
        <p className="mt-2 text-sm text-brand-muted">
          Keep the community growing — invite more thoughtful people. Or upgrade to keep Silver perks active.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/plans"
            data-testid="referral-expired-upgrade"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-4 py-2 bg-brand-terracotta text-white hover:bg-brand-terracotta/90 rounded-full"
          >
            Upgrade <ArrowRight size={14} />
          </Link>
          <Link
            to="/invite"
            data-testid="referral-expired-invite"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-4 py-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white rounded-full"
          >
            Invite more <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  // STATE 2: Reward active — celebrate
  if (reward_active) {
    return (
      <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-brand-ink text-white p-8 relative overflow-hidden" data-testid="referral-reward-active">
        <div className="absolute -right-8 -top-8 opacity-15">
          <Crown size={140} weight="duotone" className="text-brand-gold" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} weight="fill" className="text-brand-gold" />
            <div className="text-[11px] uppercase tracking-[3px] text-brand-gold font-semibold">Unlocked</div>
          </div>
          <h3 className="font-serif text-3xl mt-3 leading-tight">
            1 month of Silver,<br /><em className="text-brand-gold">on the house.</em>
          </h3>
          <p className="mt-3 text-sm text-white/70 leading-relaxed">
            You introduced <strong className="text-white">{joined} joiners</strong> — thank you for growing our community.
          </p>
          {expiryDate && (
            <div className="mt-5 text-[11px] uppercase tracking-widest text-white/50">
              Complimentary until <span className="text-brand-gold font-semibold">{expiryDate}</span>
            </div>
          )}
          <Link
            to="/invite"
            data-testid="referral-active-invite-more"
            className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest px-4 py-2 bg-brand-gold text-brand-ink hover:bg-brand-gold/90 rounded-full font-semibold"
          >
            Invite more <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  // STATE 1: In progress
  const dots = Array.from({ length: threshold }, (_, i) => i < joined);
  return (
    <div className="col-span-12 md:col-span-6 lg:col-span-4 bg-white border border-brand-line p-8 relative overflow-hidden" data-testid="referral-reward-progress">
      <div className="flex items-center gap-2">
        <Gift size={20} weight="duotone" className="text-brand-terracotta" />
        <div className="label-eyebrow text-brand-terracotta">Referral rewards</div>
      </div>
      <h3 className="font-serif text-2xl mt-3 text-brand-ink leading-tight">
        Invite <em className="text-brand-terracotta">{threshold} friends</em>,<br />get 1 month Silver free.
      </h3>

      <div className="mt-5">
        <div className="flex items-center gap-2 mb-3">
          {dots.map((filled, i) => (
            <div
              key={`progress-dot-${i}`}
              data-testid={`referral-dot-${i}`}
              className={`h-2.5 flex-1 rounded-full transition-all duration-500 ${
                filled ? "bg-brand-terracotta" : "bg-brand-line"
              }`}
            />
          ))}
        </div>
        <div className="flex items-baseline justify-between">
          <div className="font-serif text-4xl text-brand-ink tracking-tighter num-lining">
            {joined}<span className="text-brand-muted text-2xl">/{threshold}</span>
          </div>
          <div className="text-xs uppercase tracking-widest text-brand-muted">
            {remaining === 0 ? "Reward unlocking…" : `${remaining} more to unlock`}
          </div>
        </div>
      </div>

      <p className="mt-5 text-xs text-brand-muted leading-relaxed">
        Every family member or friend who accepts your invitation counts. When 3 join, we automatically activate Silver on your account for 30 days.
      </p>

      <Link
        to="/invite"
        data-testid="referral-progress-invite-cta"
        className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest px-4 py-2 bg-brand-terracotta text-white hover:bg-brand-terracotta/90 rounded-full"
      >
        Send an invite <ArrowRight size={14} />
      </Link>
    </div>
  );
}
